import DrawerBlockComponent from "../../../../uiKit/Drawer/components/DrawerBlockComponent"
import { CheckboxFormItem, formItemLayoutBalanced, renderInputFormItem } from "../../_components/SettingFormItems"
import { SettingRow } from "./SettingRow"
import { ImageUpload } from "../../../../uiKit/ImageUpload/ImageUpload"
import { CropType, ImageUploadType } from "../../../../uiKit/ImageUpload/types"
import { Form, InputNumber } from "antd"
import MibMainMenuIconSelect from "./MibMainMenuIconSelect"
import * as React from "react"
import { SettingFormItem } from "../../types"
import { WrappedFormUtils } from "antd/lib/form/Form"
import { MibLayoutSelectRow } from "./MibLayoutSelectRow"
import { EditHoursOfOperationsRow } from "./EditHoursOfOperationsRow"
import { EditSourceRow } from "./EditSourceRow"
import { DEFAULT_MIB_COLOR, MibColorRow } from "./MibColorRow"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { InputOnOffCheckbox } from "./InputOnOffCheckbox"
import { appendHostnameToImageUrl } from "../utils"
import { getFirstCroppedVariantUrl } from "../../../../uiKit/ImageUpload/utils"

type Props = {
    mainMenuIcon: string
    defaultLayoutTemplate: Maybe<number>
    isDiscountEnabled: boolean
    isFirstTimeDiscountEnabled: boolean
    isEcoFriendlyEnabled: boolean
    discountValue: Maybe<number>
    firstTimeDiscountValue: Maybe<number>
    isBannerMessageEnabled: boolean
    isAbilityToPayWithEgcEnabled: boolean
    logoUrl: Maybe<string>
    desktopImageUrl: Maybe<string>
    iconUrl: Maybe<string>
    settings: SettingFormItem
    form: WrappedFormUtils
    onStateValueChanged: (stateKey: string, value: Maybe<any>) => void
    onChangeCallback: () => void
}

export const MibOnlyGeneralSettings = (props: Props) => {
    return (
        <DrawerBlockComponent
            title="General Settings"
            style={{
                padding: 0,
            }}>
            <MibLayoutSelectRow
                defaultLayoutTemplate={props.defaultLayoutTemplate}
                onChangeCallback={props.onChangeCallback}
                onStateValueChanged={props.onStateValueChanged}
            />
            <EditHoursOfOperationsRow onChangeCallback={props.onChangeCallback} />
            <EditSourceRow />
            {CheckboxFormItem(
                props.settings,
                "MIB_HIDE_SERVICE_FROM_LOCATION_PICKER_PAGE",
                "Do not display this service in the location picker",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item",
                "Hide this service from being selected under ‘Choose location’"
            )}
            {CheckboxFormItem(
                props.settings,
                "MIB_DO_NOT_MARK_AS_CLOSED_IF_CLOSED",
                "Do not mark as 'Closed' outside of Hours of Operation",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item",
                "Recommended for catering, ticketing, and service-based businesses"
            )}
            {CheckboxFormItem(
                props.settings,
                "MIB_EMAIL_WITH_EVERY_ORDER",
                "Send Me an Email with Every New Online Order",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item",
                "Emails to Customers are sent every time"
            )}
            {CheckboxFormItem(
                props.settings,
                "MIB_AUTO_ACCEPT_ALL",
                "Automatically Accept All Online Orders",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item",
                null
            )}
            {props.form.getFieldValue("MIB_AUTO_ACCEPT_ALL")
                ? CheckboxFormItem(
                      props.settings,
                      "MIB_AUTO_ACCEPT_ONLY_WHEN_SIGNED_IN",
                      "Automatically Accept Online Orders only when Register is Signed In",
                      props.form,
                      {
                          marginLeft: "4rem",
                      },
                      props.onChangeCallback,
                      "receipt-setting-form-item",
                      null
                  )
                : null}
            {CheckboxFormItem(
                props.settings,
                "MIB_AUTO_CLOSE",
                "Automatically Close 'Paid Orders' upon Acceptance of Online Order",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item",
                null
            )}
            {CheckboxFormItem(
                props.settings,
                "MIB_ABILITY_TO_DELAY_ORDERS_ENABLED",
                "Enable ability to 'delay orders' for 'Pick Up' and 'Delivery' orders",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item",
                "Allows you to delay orders when your business is busy. This will also communicate the delay to your customers."
            )}
            <MibColorRow settings={props.settings} form={props.form} onChangeCallback={props.onChangeCallback} />
            <SettingRow
                label="Custom Logo"
                helpText="This image will represent your business throughout the ordering website">
                <ImageUpload
                    type={ImageUploadType.Single}
                    className={classes.imageUpload}
                    offerImageCrop={true}
                    imageOptions={{
                        maxWidth: 1800,
                        maxHeight: 300,
                    }}
                    cropModalTitle="Crop logo image"
                    value={
                        props.logoUrl
                            ? [
                                  {
                                      originalImage: {
                                          id: "mib-logo",
                                          url: appendHostnameToImageUrl(props.logoUrl) as string,
                                      },
                                  },
                              ]
                            : []
                    }
                    onChange={result => {
                        props.onChangeCallback()
                        props.onStateValueChanged("logoUrl", getFirstCroppedVariantUrl(result))
                    }}
                />
            </SettingRow>
            <SettingRow
                label="Custom Icon"
                helpText="This image will be used when customers save the website to Favorites or Home Screen.">
                <ImageUpload
                    className={classes.imageUpload}
                    type={ImageUploadType.Single}
                    cropModalTitle="Crop icon image"
                    imageOptions={{
                        maxWidth: 512,
                        maxHeight: 512,
                        minWidth: 512,
                        minHeight: 512,
                    }}
                    offerImageCrop={true}
                    cropOptions={[
                        {
                            defaultWidth: 512,
                            defaultHeight: 512,
                            cropType: CropType.Ratio_1_1,
                        },
                    ]}
                    value={
                        props.iconUrl
                            ? [
                                  {
                                      originalImage: {
                                          id: "mib-icon",
                                          url: appendHostnameToImageUrl(props.iconUrl) as string,
                                      },
                                  },
                              ]
                            : []
                    }
                    onChange={result => {
                        props.onChangeCallback()
                        props.onStateValueChanged("iconUrl", getFirstCroppedVariantUrl(result))
                    }}
                />
            </SettingRow>
            <SettingRow
                label="Desktop web header hero image"
                helpText="This image will be visible on desktop version, in the header. Recommended size: 1920 x 300 pixels">
                <ImageUpload
                    className={classes.imageUpload}
                    type={ImageUploadType.Single}
                    cropModalTitle="Crop desktop hero image"
                    imageOptions={{
                        forceJpeg: true,
                        maxWidth: 1920,
                        maxHeight: 300,
                        minWidth: 1920,
                        minHeight: 300,
                    }}
                    offerImageCrop={true}
                    cropOptions={[
                        {
                            defaultWidth: 1920,
                            defaultHeight: 300,
                            locked: true,
                            cropType: CropType.Free,
                        },
                    ]}
                    value={
                        props.desktopImageUrl
                            ? [
                                  {
                                      originalImage: {
                                          id: "mib-bg",
                                          url: appendHostnameToImageUrl(props.desktopImageUrl) as string,
                                      },
                                  },
                              ]
                            : []
                    }
                    onChange={result => {
                        props.onChangeCallback()
                        props.onStateValueChanged("desktopImageUrl", getFirstCroppedVariantUrl(result))
                    }}
                />
            </SettingRow>
            {CheckboxFormItem(
                props.settings,
                "MIB_DESKTOP_DISPLAY_BUSINESS_INFO_BOX",
                "Display business name and details on hero image",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item"
            )}
            <div>
                <Form.Item
                    style={{
                        marginBottom: "0rem",
                    }}
                    className="receipt-setting-form-item"
                    {...formItemLayoutBalanced}
                    label="Main Menu Type">
                    <MibMainMenuIconSelect
                        iconColor={
                            props.form.getFieldValue("MIB_PRIMARY_COLOR") ||
                            props.settings.MIB_PRIMARY_COLOR.value ||
                            DEFAULT_MIB_COLOR
                        }
                        value={props.mainMenuIcon}
                        onChangeCallback={value => {
                            props.onStateValueChanged("mainMenuIcon", value)
                            props.onChangeCallback()
                        }}
                    />
                </Form.Item>
            </div>
            <InputOnOffCheckbox
                settings={props.settings}
                key="MIB_BANNER_MESSAGE"
                stateKey="isBannerMessageEnabled"
                stateFieldValue={props.isBannerMessageEnabled}
                label="Banner Messaging"
                helperText="Promotional messaging for your customers. Will be displayed on top of ordering page. Limited to 90 characters."
                onChangeCallback={props.onChangeCallback}
                onStateValueChanged={props.onStateValueChanged}
                className="receipt-setting-form-item"
            />
            {props.isBannerMessageEnabled && (
                <div
                    style={{
                        marginBottom: "0.5rem",
                        marginLeft: "2rem",
                        marginTop: "-0.5rem",
                    }}>
                    {renderInputFormItem(
                        props.settings,
                        "MIB_BANNER_MESSAGE",
                        " ",
                        "Enter message",
                        props.form,
                        {
                            borderTop: "none",
                        },
                        props.onChangeCallback,
                        "receipt-setting-form-item promo-code-word",
                        null,
                        false,
                        null,
                        {
                            width: "16rem",
                            textAlign: "left",
                            position: "relative",
                            top: "4px",
                        },
                        {},
                        {
                            maxLength: 90,
                        }
                    )}
                </div>
            )}
            <InputOnOffCheckbox
                settings={props.settings}
                key="MIB_ECO_FRIENDLY_OPTION_ENABLED"
                stateKey="isEcoFriendlyEnabled"
                stateFieldValue={props.isEcoFriendlyEnabled}
                label="Enable eco-friendly option during customer checkout"
                helperText='Inquires customers to "Go Green! Do not include plastic utensils"'
                onChangeCallback={props.onChangeCallback}
                onStateValueChanged={props.onStateValueChanged}
                className="receipt-setting-form-item"
            />
            {props.isEcoFriendlyEnabled && (
                <div className={classes.padLeft}>
                    {CheckboxFormItem(
                        props.settings,
                        "MIB_ECO_FRIENDLY_OPTION_DEFAULT_CHECKED",
                        "Prompt eco-friendly option as default behavior",
                        props.form,
                        null,
                        props.onChangeCallback,
                        "receipt-setting-form-item",
                        "Customers will need to deselect eco-friendly as an option if they require utensils"
                    )}
                </div>
            )}
            <InputOnOffCheckbox
                settings={props.settings}
                key="MIB_PROMO_CODE_SP_ID"
                stateKey="isDiscountEnabled"
                stateFieldValue={props.isDiscountEnabled}
                label="Promo Discount"
                helperText="Set up Promo Discount (e.g -10%)"
                onChangeCallback={props.onChangeCallback}
                onStateValueChanged={props.onStateValueChanged}
                className="receipt-setting-form-item"
            />
            {props.isDiscountEnabled && (
                <>
                    <Form.Item
                        style={{
                            marginLeft: "3rem",
                            marginBottom: "0rem",
                            borderTop: "none",
                        }}
                        className="receipt-setting-form-item"
                        label="Promo Discount percentage"
                        {...formItemLayoutBalanced}>
                        <InputNumber
                            style={{
                                width: "12rem",
                                marginRight: 0,
                            }}
                            defaultValue={props.discountValue || undefined}
                            value={
                                (props.discountValue != null ? props.discountValue : props.discountValue) || undefined
                            }
                            formatter={value => `${value}%`}
                            onFocus={e => e.target.select()}
                            placeholder="-10%"
                            parser={value => (value || "").replace("%", "")}
                            onChange={val => {
                                props.onStateValueChanged("discountValue", -Math.abs(val ?? 0))
                                props.onChangeCallback()
                            }}
                        />
                    </Form.Item>
                    <div
                        style={{
                            marginBottom: "0.5rem",
                            marginLeft: "2rem",
                        }}>
                        {renderInputFormItem(
                            props.settings,
                            "MIB_PROMO_CODE_WORD",
                            "Promo Discount Code",
                            "Enter word",
                            props.form,
                            {},
                            props.onChangeCallback,
                            "receipt-setting-form-item promo-code-word",
                            "Please enter text required for customers to receive promotional discount",
                            false,
                            null,
                            {
                                width: "12rem",
                                textAlign: "left",
                                position: "relative",
                                top: "4px",
                            },
                            {
                                rules: [
                                    {
                                        required: true,
                                        message: "Promo word cannot be empty",
                                    },
                                ],
                            }
                        )}
                    </div>
                </>
            )}
            <InputOnOffCheckbox
                settings={props.settings}
                key="MIB_ABILITY_TO_PAY_WITH_EGC"
                stateKey="isAbilityToPayWithEgcEnabled"
                stateFieldValue={props.isAbilityToPayWithEgcEnabled}
                label="Enable ability to pay with Gift Cards"
                onChangeCallback={props.onChangeCallback}
                onStateValueChanged={props.onStateValueChanged}
                className="receipt-setting-form-item"
            />
            <InputOnOffCheckbox
                settings={props.settings}
                key="MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID"
                stateKey="isFirstTimeDiscountEnabled"
                stateFieldValue={props.isFirstTimeDiscountEnabled}
                label="Enable discount for first time customers (customer must sign up new account)"
                helperText="Set up discount (e.g -10%)"
                onChangeCallback={props.onChangeCallback}
                onStateValueChanged={props.onStateValueChanged}
                className="receipt-setting-form-item"
            />
            {props.isFirstTimeDiscountEnabled && (
                <Form.Item
                    style={{
                        marginLeft: "3rem",
                        marginBottom: "1rem",
                        borderTop: "none",
                    }}
                    className="receipt-setting-form-item"
                    label="Discount percentage"
                    {...formItemLayoutBalanced}>
                    {/* @ts-expect-error */}
                    <InputNumber
                        style={{
                            width: "12rem",
                            marginRight: 0,
                        }}
                        defaultValue={props.firstTimeDiscountValue}
                        value={props.firstTimeDiscountValue}
                        formatter={value => `${value}%`}
                        onFocus={e => e.target.select()}
                        placeholder="-10%"
                        parser={value => (value || "").replace("%", "")}
                        onChange={(val: number) => {
                            props.onStateValueChanged("firstTimeDiscountValue", -Math.abs(val))
                            props.onChangeCallback()
                        }}
                    />
                </Form.Item>
            )}
            {CheckboxFormItem(
                props.settings,
                "FUTURE_ORDERING_DATE_ONLY",
                "Enable Date Only for Future Orders",
                props.form,
                null,
                props.onChangeCallback,
                "receipt-setting-form-item",
                "Future orders will be scheduled for a specific date without a specified time"
            )}
        </DrawerBlockComponent>
    )
}

const classes = {
    imageUpload: "w-[250px] max-h-[70px]",
    padLeft: "ml-8",
}
